'use client';

import { useEffect, useState } from 'react';
import { GoogleAnalytics } from '@next/third-parties/google';
import { analyticsAllowed, CONSENT_CHANGED_EVENT } from '@/lib/consent';

/**
 * Consent gate for Google Analytics (Poland RODO/GDPR).
 *
 * GoogleAnalytics is rendered ONLY while the visitor has accepted analytics
 * cookies. It starts denied — on the server and the first client paint — so the
 * gtag script never loads before a choice is made. It then reads the saved
 * decision and re-evaluates live whenever consent changes (CONSENT_CHANGED_EVENT
 * from the cookie banner), so toggling consent mounts/unmounts GA without a reload.
 */
export default function AnalyticsGate({ gaId }: { gaId: string }) {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const sync = () => setAllowed(analyticsAllowed());
    sync(); // apply the saved decision on mount
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
  }, []);

  // Belt-and-braces kill-switch. Unmounting <GoogleAnalytics> does NOT remove the
  // gtag.js already injected by next/script, so on withdrawal gtag would keep
  // running. Google's `ga-disable-<id>` flag stops it from sending hits or
  // re-setting cookies until consent is granted again.
  useEffect(() => {
    (window as unknown as Record<string, boolean>)[`ga-disable-${gaId}`] = !allowed;
  }, [allowed, gaId]);

  return allowed ? <GoogleAnalytics gaId={gaId} /> : null;
}
